import React, { useState, useEffect } from 'react';
import "./FeedbackBody.css";
import { useStateValue } from "../StateProvider";
import SongBox from "./SongBoxes/SongBox";
import FBGetSongsFromDB from "./FBGetSongsFromDB.js";
import "../AddMySong/TrackResult.css";
import ChooseSong from "../AddMySong/ChooseSong";
import SpotLogoLogin from "../img/Spotify_Logo_Login.png";
import NoMoreSongsToRate from "./NoMoreSongsToRate";
import GenreFilter from "./GenreFilter";

import { authV9, firestore } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getFirestore, doc, updateDoc, setDoc, arrayUnion } from 'firebase/firestore';

function FeedbackCarouselBodyUserFetched({ spotify, userid, useremail }) {
  const [{ menustate, countUserRatings, token, dblesenerlauben, packageOfSongsTorate, dblesencomingfrommenu, subscriptionConfig, userTier, dbUserRatings, songsRated, userEmail, selectedGenreFilter, songToPlay }, dispatch] = useStateValue();

  // Firebase auth (v9) hook - must be at top level
  const [firebaseUser, loadingAuth] = useAuthState(authV9);

  const anzahlSongsZufallAuswahl = [5, 5];

  const [onlySetAllUserEmailsOnce, setOnlySetAllUserEmailsOnce] = useState(false);
  const [anzahlSongsZufallIndex, setAnzahlSongsZufallIndex] = useState(0);

  // initial: pick random index
  useEffect(() => {
    setAnzahlSongsZufallIndex(Math.floor(Math.random() * anzahlSongsZufallAuswahl.length));
    // User is already authenticated via Firebase Auth - no need for anonymous sign-in
  }, [anzahlSongsZufallAuswahl.length]);

  // Restore currently playing song state after page reload
  useEffect(() => {
    // Only restore if there's no song currently playing and we have a token
    if (songToPlay === "0" && token) {
      try {
        const stored = localStorage.getItem('currentlyPlayingSong');
        if (stored) {
          const parsedSong = JSON.parse(stored);
          // Check if song was saved within the last 24 hours (prevent stale entries)
          const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
          if (parsedSong.savedAt && parsedSong.savedAt > twentyFourHoursAgo && parsedSong.trackid) {
            // Check if the song hasn't been rated yet
            if (!songsRated[parsedSong.trackid]) {
              console.log(`🔄 Restoring currently playing song from localStorage: ${parsedSong.trackid}`);

              // Set the song to play
              dispatch({
                type: "SET_SONGTOPLAY",
                songToPlay: parsedSong.trackid,
              });

              // Show the Spotify Player UI
              dispatch({
                type: "SET_SHOWSPOTIFYPLAYER",
                showSpotifyPlayer: 1,
              });

              // Set play blocker to show the song is active
              dispatch({
                type: "SET_PLAYBLOCKER",
                playBlocker: 1,
              });

              // Show initialize player button
              dispatch({
                type: "SET_SHOWINITIALIZEPLAYERBUTTON",
                showInitializePlayerButton: 1,
              });

              // Show restart button so user can reconnect to Spotify after reload
              dispatch({
                type: "SET_SHOWRESTARTBUTTON",
                showRestartButton: true,
              });

              console.log('🔄 Restored song - user needs to click RESUME PLAYBACK to reconnect to Spotify');
            } else {
              // Song has been rated, clear from localStorage
              localStorage.removeItem('currentlyPlayingSong');
              console.log('🗑️ Cleared rated song from localStorage');
            }
          } else {
            // Clear stale entry
            localStorage.removeItem('currentlyPlayingSong');
            console.log('🗑️ Cleared stale currently playing song from localStorage');
          }
        }
      } catch (e) {
        console.error("Failed to restore currently playing song from localStorage:", e);
      }
    }
  }, [songToPlay, token, songsRated, dispatch]);

  // Load previously rated songs from Firestore when user logs in
  useEffect(() => {
    if (!userid || loadingAuth || !firebaseUser) return;

    const loadPreviouslyRatedSongs = async () => {
      try {
        // Query userRatings collection for songs rated by this user
        const ratingsQuery = firestore
          .collection(dbUserRatings)
          .where('fromspotifyuser', '==', userid);

        const snapshot = await ratingsQuery.get();

        if (!snapshot.empty) {
          const ratedSongs = {};
          snapshot.forEach(doc => {
            const data = doc.data();
            // Store the track ID and rating value
            if (data.trackIdOfSong) {
              ratedSongs[data.trackIdOfSong] = data.rating || 1;
            }
          });

          // Dispatch all previously rated songs to state
          Object.keys(ratedSongs).forEach(songId => {
            dispatch({
              type: "SET_SONGRATED",
              songId: songId,
              rating: ratedSongs[songId],
            });
          });

          console.log(`✅ Loaded ${Object.keys(ratedSongs).length} previously rated songs for user ${userid}`);
        }
      } catch (error) {
        console.error('❌ Error loading previously rated songs:', error);
      }
    };

    loadPreviouslyRatedSongs();
  }, [userid, firebaseUser, loadingAuth, dbUserRatings, dispatch]);

  // write user email (once) into allEmailsColl using modular Firestore
  useEffect(() => {
    // Wait for Firebase auth to complete before writing email
    if (loadingAuth || !firebaseUser) return;
    if (dblesenerlauben !== 1 || dblesencomingfrommenu !== 1) return;
    if (onlySetAllUserEmailsOnce) return;

    const writeEmail = async () => {
      const db = getFirestore();
      const allEmailsDocRef = doc(db, "allEmailsColl", "allEmailsDoc");
      const ratingDateToString = new Intl.DateTimeFormat('en-US', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).format(new Date());
      const entry = `${ratingDateToString} | ${useremail || 'no-email'}`;

      try {
        await updateDoc(allEmailsDocRef, {
          emailsLogin: arrayUnion(entry),
        });
      } catch (err) {
        // if document does not exist -> create it
        const notFound = err && (err.code === 'not-found' || (err.message && err.message.toLowerCase().includes('no document')));
        if (notFound) {
          try {
            await setDoc(allEmailsDocRef, { emailsLogin: [entry] }, { merge: true });
          } catch (e) {
            console.error('Fehler beim Erstellen des allEmailsDoc:', e);
          }
        } else {
          console.error('Fehler beim Schreiben in allEmailsColl:', err);
        }
      } finally {
        setOnlySetAllUserEmailsOnce(true);
      }
    };

    writeEmail();
  }, [firebaseUser, loadingAuth, dblesenerlauben, dblesencomingfrommenu, useremail, onlySetAllUserEmailsOnce]);

  // Filter songs by genre on the client side
  const filterSongsByGenre = (songs, selectedGenre) => {
    if (!songs) return null;

    // If "All Genres" is selected, return all songs
    if (selectedGenre === "All Genres") {
      console.log(`Genre filter: All Genres selected, showing ${songs.length} songs`);
      return songs;
    }

    // Filter songs by the selected genre
    const filtered = songs.filter(song => {
      const songGenre = song.genre || "None";
      return songGenre === selectedGenre;
    });

    console.log(`Genre filter: ${selectedGenre} selected, filtered from ${songs.length} to ${filtered.length} songs`);
    return filtered;
  };

  // selection of tracks from state (filtered by genre)
  const selectionOfTracks = filterSongsByGenre(packageOfSongsTorate, selectedGenreFilter);

  // navigate to add own song when ratings done
  const songsToRateBeforeUpload = subscriptionConfig?.songsToRateBeforeUpload || 6; // default to free tier

  // Track both total (unfiltered) and filtered song counts
  const totalAvailableSongs = packageOfSongsTorate ? packageOfSongsTorate.length : 0;
  const filteredAvailableSongs = selectionOfTracks ? selectionOfTracks.length : 0;

  // Use total songs for availability, filtered songs for display
  const availableSongs = filteredAvailableSongs;

  // Check if genre filter is limiting songs below the rating requirement
  const isFilterLimitingSongs = selectedGenreFilter !== "All Genres" &&
                                 filteredAvailableSongs < songsToRateBeforeUpload &&
                                 totalAvailableSongs >= songsToRateBeforeUpload;

  // Check if song selection has been loaded (null means not loaded yet, array means loaded)
  const songsLoaded = packageOfSongsTorate !== null;

  // Check if user has uploaded at least one song before
  const hasUploadedBefore = localStorage.getItem('hasUploadedSong') === 'true';

  // Check if all available songs have been rated (no more songs to show)
  // Use totalAvailableSongs to ensure we only show this when there truly are no songs,
  // not when the genre filter is hiding them
  const allSongsRated = songsLoaded && totalAvailableSongs === 0 && Object.keys(songsRated).length > 0;

  // Determine if this qualifies as a "limited songs" upload scenario (use TOTAL songs, not filtered)
  // Free users: 4 or fewer songs, Premium users: 3 or fewer songs
  const isLimitedSongsScenario = hasUploadedBefore && (
    (userTier === 'free' && totalAvailableSongs > 0 && totalAvailableSongs <= 4) ||
    (userTier === 'premium' && totalAvailableSongs > 0 && totalAvailableSongs <= 3)
  );

  // Zero songs scenario (also counts as limited for subsequent uploads)
  const isZeroSongsScenario = hasUploadedBefore && totalAvailableSongs === 0;

  // Allow upload if:
  // FIRST UPLOAD:
  // 1. User has rated the required number of songs (standard threshold)
  // SUBSEQUENT UPLOADS:
  // 2. Limited songs scenario: User has rated all available songs (use TOTAL, not filtered)
  // 3. Zero songs scenario: Can upload immediately
  // IMPORTANT: Only check this AFTER songs have been loaded
  // IMPORTANT: Filter must NOT reduce the requirement - if filter is active and limiting songs, block upload
  const canUpload = songsLoaded && !isFilterLimitingSongs && (
    (!hasUploadedBefore && countUserRatings >= songsToRateBeforeUpload) || // First upload: standard threshold
    (hasUploadedBefore && countUserRatings >= songsToRateBeforeUpload) || // Subsequent upload: standard threshold met
    (isLimitedSongsScenario && countUserRatings >= totalAvailableSongs) || // Limited songs: rated all available (TOTAL)
    isZeroSongsScenario // Zero songs: immediate upload
  );

  // Handle navigation to add own song (moved to useEffect to avoid dispatch during render)
  useEffect(() => {
    if (canUpload) {
      // Set the limited songs flag if this is a limited songs or zero songs scenario
      const shouldShowNoMoreSongs = isLimitedSongsScenario || isZeroSongsScenario;
      dispatch({
        type: "SET_ISLIMITEDSONGSUPLOAD",
        isLimitedSongsUpload: shouldShowNoMoreSongs
      });

      dispatch({ type: "SET_RATINGSNUREINMALCALC", nurEinmalRatngsCalc: 0 });
      dispatch({ type: "SET_MENUSTATE", menustate: "addownsong" });
    }
  }, [canUpload, isLimitedSongsScenario, isZeroSongsScenario, dispatch]);

  // Render ChooseSong if we're in addownsong mode
  if (token && menustate === "addownsong") {
    return <ChooseSong />;
  }

  // Show NoMoreSongsToRate component if all available songs have been rated
  if (allSongsRated) {
    return <NoMoreSongsToRate />;
  }

  // Handle Manage Subscription
  const handleManageSubscription = async () => {
    if (!userEmail) {
      alert('No email found. Please contact support.');
      return;
    }

    console.log('Opening subscription management for email:', userEmail);

    try {
      const response = await fetch('https://createportalsession-u4auismf4q-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: userEmail }),
      });

      console.log('Portal response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Portal error response:', errorData);

        if (response.status === 404) {
          alert(`No subscription found for email: ${userEmail}\n\nPlease make sure you subscribed with this email address. If you used a different email, please contact support at catchyrecords@gmail.com`);
        } else {
          alert(`Error: ${errorData.error || 'Failed to create portal session'}\n\nPlease contact support at catchyrecords@gmail.com`);
        }
        return;
      }

      const data = await response.json();
      console.log('Portal URL:', data.url);
      window.location.href = data.url;
    } catch (error) {
      console.error('Error opening customer portal:', error);
      alert('Failed to open subscription management. Please contact support at catchyrecords@gmail.com');
    }
  };

  return (
    <div className="feedbackBody">
      {dblesenerlauben === 1 && dblesencomingfrommenu === 1 && (
        <FBGetSongsFromDB userid={userid} anzahlSongsZufall={anzahlSongsZufallAuswahl[anzahlSongsZufallIndex]} authReady={!loadingAuth && !!firebaseUser} />
      )}

      {/* Genre Filter - shown after songs are loaded (stays visible even if filtered results are empty) */}
      {songsLoaded && totalAvailableSongs > 0 && <GenreFilter />}

      <h2>
        <span role="img" aria-label="headphones">🎧</span> PLAY and RATE{" "}
        {!songsLoaded ? (
          // Show loading message while songs are being fetched
          <>
            <span style={{ color: '#1db954' }}>Loading songs to rate... please wait</span>
            <br />
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: '10px',
                padding: '8px 20px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
              }}
            >
              Reload songs manually
            </button>
          </>
        ) : availableSongs === 0 ? (
          // Show "no songs" message - different message if it's due to filtering
          <span>
            {selectedGenreFilter !== "All Genres" && totalAvailableSongs > 0 ? (
              <>
                <span className="redfont">No songs in the "{selectedGenreFilter}" genre</span>
                <br />
                <span style={{ fontSize: '14px', color: '#666' }}>Please select a different genre from the filter above</span>
              </>
            ) : (
              <>
                <span className="redfont">No songs available to rate</span>
                <br />
                <button
                  onClick={() => dispatch({ type: "SET_MENUSTATE", menustate: "addownsong" })}
                  style={{
                    marginTop: '10px',
                    padding: '8px 20px',
                    backgroundColor: '#1db954',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(29, 185, 84, 0.3)',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(29, 185, 84, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(29, 185, 84, 0.3)';
                  }}
                >
                  Upload Your Song
                </button>
              </>
            )}
          </span>
        ) : (
          <>
            <span className="redfont">
              {Math.min(songsToRateBeforeUpload - countUserRatings, availableSongs - countUserRatings)}
            </span>{" "}
            {availableSongs < songsToRateBeforeUpload ? (
              <>song{Math.min(songsToRateBeforeUpload - countUserRatings, availableSongs - countUserRatings) === 1 ? '' : 's'} (only {availableSongs} available) before you can submit your song</>
            ) : (
              <>song{songsToRateBeforeUpload - countUserRatings === 1 ? '' : 's'} before you can submit your song</>
            )}
          </>
        )}{" "}
        <span role="img" aria-label="headphones">🎧</span>
      </h2>

      {/* Show warning when genre filter is limiting available songs below upload requirement */}
      {isFilterLimitingSongs && (
        <div style={{
          textAlign: 'center',
          margin: '15px auto',
          padding: '15px 20px',
          backgroundColor: '#fee2e2',
          border: '2px solid #ef4444',
          borderRadius: '12px',
          maxWidth: '600px',
        }}>
          <p style={{
            margin: 0,
            fontSize: '14px',
            color: '#991b1b',
            fontWeight: '600'
          }}>
            ⚠️ Please reset the filter in order to rate more songs before you can upload your song.
          </p>
          <p style={{
            margin: '8px 0 0 0',
            fontSize: '12px',
            color: '#991b1b',
          }}>
            You need to rate {songsToRateBeforeUpload} songs total, but the current filter only shows {filteredAvailableSongs} song{filteredAvailableSongs === 1 ? '' : 's'}.
          </p>
        </div>
      )}

      {/* Show Manage Subscription button for Premium users */}
      {userTier === 'premium' && (
        <div style={{ textAlign: 'left', marginTop: '15px', marginBottom: '15px' }}>
          <button
            onClick={handleManageSubscription}
            style={{
              padding: '10px 24px',
              backgroundColor: '#000000',
              color: '#ffffff',
              border: '1px solid #ffffff',
              borderRadius: '0',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: 'none',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#ff1493';
              e.target.style.color = '#ff1493';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#ffffff';
              e.target.style.color = '#ffffff';
            }}
          >
            Manage Subscription
          </button>
          {subscriptionConfig?.cancelAt && (
            <div style={{
              marginTop: '10px',
              fontSize: '12px',
              color: '#f59e0b',
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              padding: '8px 12px',
              borderRadius: '6px',
              borderLeft: '3px solid #f59e0b',
              maxWidth: '400px'
            }}>
              Subscription will be cancelled on {new Date(subscriptionConfig.cancelAt * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          )}
        </div>
      )}

{userTier === 'free' && songsLoaded && (
<div>


         <button
            onClick={() => window.dispatchEvent(new Event('openSubscription'))}
            style={{
              padding: '10px 24px',
              backgroundColor: '#90ee90',
              color: '#000000',
              border: 'none',
              borderRadius: '0',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: 'none',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'none';
              e.target.style.boxShadow = 'none';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'none';
              e.target.style.boxShadow = 'none';
            }}
          >
            Upgrade to Premium
          </button>
</div>

 )}
    
      <div className="songBoxesContainer">
        {selectionOfTracks && selectionOfTracks.map(track => (
          <SongBox TrackForBox={track} key={track.id} spotify={spotify} />
        ))}
      </div>
  {userTier === 'free' && songsLoaded && (
        <div style={{
          textAlign: 'center',
          margin: '15px auto 25px',
          padding: '15px 20px',
          backgroundColor: '#333333',
          border: '1px solid #ffffff',
          borderRadius: '0',
          maxWidth: '600px',
        }}>
          <p style={{
            margin: '0 0 12px 0',
            fontSize: '14px',
            color: '#ffffff',
            fontWeight: '500'
          }}>
            🌟 <strong>Upgrade to Premium</strong> to rate songs after only <strong>34 seconds</strong> (instead of {subscriptionConfig?.listeningTimeRequired}s)
            and upload after rating just <strong>4 songs</strong> (instead of {songsToRateBeforeUpload})!
          </p>
          <button
            onClick={() => window.dispatchEvent(new Event('openSubscription'))}
            style={{
              padding: '10px 24px',
              backgroundColor: '#90ee90',
              color: '#000000',
              border: 'none',
              borderRadius: '0',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: 'none',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'none';
              e.target.style.boxShadow = 'none';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'none';
              e.target.style.boxShadow = 'none';
            }}
          >
            👑 Upgrade to Premium - Only $2.99/month
          </button>
        </div>
      )}

      <div className="songsFromSpot">
        Song Images and Infos coming from:<br />
        <img src={SpotLogoLogin} alt="Spotify Logo" />
      </div>
    </div>
  );
}

export default FeedbackCarouselBodyUserFetched;